<template>
  <modal-container :isModalActive="activeModal" v-on:closeModal="close">
    <div v-if="activeModal" class="fh" slot="modal-content">
      <div class="content-layout fh">
        <div style="margin: 0px 16px 16px;" class="results-left fh rb shadow">
          <div>
            <div class="bold">Assessment Results</div>
            <div class="component-sub-header">
              {{
                `${contextualGradeLabel()} ${
                  active.class ? active.class : active.grade
                } ${active.thread} Results Per Concept`
              }}
            </div>
          </div>
          <div v-if="drChart.data" style="margin-top: 2.5rem">
            <horizontal-bar-chart
              style="width: 100%; position: relative"
              ref="bigChart"
              chart-id="detailed-results-chart"
              :chart-data="drChart.data"
              :extra-options="drChart.extraOptions"
            >
            </horizontal-bar-chart>
          </div>
        </div>
        <div class="results-right fh rb shadow">
          <div class="modal-table">
            <div class="bold">
              {{ `Students` }}
            </div>
            <div class="fh" style="width: 100%; position: relative;">
              <learner-table
                :index="1"
                :users="studentsList"
                style="height: 100%; box-shadow: inset 0 -20px 20px -20px rgba(50,50,50,0.2);"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal-container>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import ModalContainer from "@/modules/global/containers/ModalContainer.vue";
import LearnerTable from "@/modules/global/components/TableComponent.vue";
import TinyBoxComponent from "@/modules/global/components/TinyBoxComponent.vue";
import BarChart from "@/modules/global/lib/charts/BarChart.js";
import HorizontalBarChart from "@/modules/global/lib/charts/HorizontalBarChart.js";
import { chartColorsMixin } from "@/modules/global/mixins/chartColorsMixin.js";
//@refactor for multiple subjects
import matric from "@/modules/global/utils/thread-layouts/matric-foundation.json";
import juniorHigh from "@/modules/global/utils/thread-layouts/junior-high.json";
import seniorPrimary from "@/modules/global/utils/thread-layouts/senior-primary.json";
import juniorPrimary from "@/modules/global/utils/thread-layouts/junior-primary.json";
import adultNumeracy from "@/modules/global/utils/thread-layouts/adult-numeracy.json";
import englishJuniorHigh from "@/modules/global/utils/thread-layouts/english-junior-high.json";
import englishSeniorPrimary from "@/modules/global/utils/thread-layouts/english-senior-primary.json";

export default {
  name: "ResultsBreakdown",
  props: {
    studentsList: Array
  },
  data() {
    return {
      drChart: {
        data: null,
        extraOptions: null
      },
      baseStackedChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
          labels: {
            usePointStyle: true,
            boxWidth: 8
          }
        },
        responsive: true
      }
    };
  },
  mixins: [chartColorsMixin],
  components: {
    "modal-container": ModalContainer,
    "learner-table": LearnerTable,
    "horizontal-bar-chart": HorizontalBarChart
  },
  computed: {
    ...mapState({
      active: state => state.globalStore.activeFilterParams,
      activeModal: state =>
        state.globalStore.settings.isResultsBreakdownModalActive
    }),
    concepts() {
      if (this.active.assessmentType === "MATHS_SENIOR_PRIMARY") {
        return seniorPrimary[this.active.threadId];
      } else if (this.active.assessmentType === "MATHS_JUNIOR_PRIMARY") {
        return juniorPrimary[this.active.threadId];
      } else if (this.active.assessmentType === "MATHS_MATRIC_FOUNDATIONS") {
        return matric[this.active.threadId];
      } else if (this.active.assessmentType === "MATHS_ADULT_NUMERACY") {
        return adultNumeracy[this.active.threadId];
      } else if (this.active.assessmentType === "ENGLISH_JUNIOR_HIGH") {
        return englishJuniorHigh[this.active.threadId];
      } else if (this.active.assessmentType === "ENGLISH_SENIOR_PRIMARY") {
        return englishSeniorPrimary[this.active.threadId];
      } else {
        return juniorHigh[this.active.threadId + 1];
      }
    },
    gradeIntervals() {
      let intervals = {
        "0": "",
        "1": "",
        "2": "",
        "3": ""
      };
      if (this.active.assessmentType === "MATHS_JUNIOR_PRIMARY") {
        intervals["0"] = `Below ${this.contextualSpecificGradeAndLabel(1)}`;
        intervals["1"] = `${this.contextualSpecificGradeAndLabel(1)}`;
        intervals["2"] = `${this.contextualSpecificGradeAndLabel(2)}`;
        intervals["3"] = `${this.contextualSpecificGradeAndLabel(3)}`;
      } else if (
        this.active.assessmentType === "MATHS_SENIOR_PRIMARY" ||
        this.active.assessmentType === "ENGLISH_SENIOR_PRIMARY"
      ) {
        intervals["0"] = `Below ${this.contextualSpecificGradeAndLabel(1)}`;
        intervals["1"] = `${this.contextualSpecificGradeAndLabel(
          1
        )} - ${this.contextualSpecificGrade(3)}`;
        intervals["2"] = `${this.contextualSpecificGradeAndLabel(
          4
        )} - ${this.contextualSpecificGrade(5)}`;
        intervals["3"] = `${this.contextualSpecificGradeAndLabel(6)}`;
      } else {
        intervals["0"] = `Below ${this.contextualSpecificGradeAndLabel(3)}`;
        intervals["1"] = `${this.contextualSpecificGradeAndLabel(
          3
        )} - ${this.contextualSpecificGrade(5)}`;
        intervals["2"] = `${this.contextualSpecificGradeAndLabel(
          6
        )} - ${this.contextualSpecificGrade(8)}`;
        intervals["3"] = `${this.contextualSpecificGradeAndLabel(9)}`;
      }
      return intervals;
    },
    totalLearners() {
      return this.studentsList.length;
    },
    conceptLevels() {
      let conceptList = [];
      for (let i = 1; i <= this.concepts.length; i++) {
        let cncpt = this.concepts.find(concept => concept.concept == i);
        conceptList.push(cncpt.name);
      }
      return conceptList;
    },
    levelZero() {
      return this.filterByLevel(0);
    },
    levelOne() {
      return this.filterByLevel(1);
    },
    levelTwo() {
      return this.filterByLevel(2);
    },
    levelThree() {
      return this.filterByLevel(3);
    }
  },
  methods: {
    ...mapMutations({
      isModalActive: "globalStore/updateResultsBreackdownModalActive"
    }),
    close() {
      this.isModalActive(false);
    },
    filterByLevel(level) {
      let conceptTotals = [];
      for (let i = 0; i < this.concepts.length; i++) {
        let userlist = this.studentsList.filter(
          user =>
            user.catchupStates[0].threads[this.active.threadId].conceptMastery[
              i
            ] === level
        ).length;
        conceptTotals.push(userlist);
      }
      return conceptTotals;
    },
    horizontalStackedChart() {
      // console.log("What are the concepts: ", this.levelThree);
      let data = {
        labels: this.conceptLevels,
        datasets: [
          {
            label: this.gradeIntervals["0"],
            data: this.levelZero,
            backgroundColor: () => {
              return this.red;
            }
          },
          {
            label: this.gradeIntervals["1"],
            data: this.levelOne,
            backgroundColor: () => {
              return this.orange;
            }
          },
          {
            label: this.gradeIntervals["2"],
            data: this.levelTwo,
            backgroundColor: () => {
              return this.yellow;
            }
          },
          {
            label: this.gradeIntervals["3"],
            data: this.levelThree,
            backgroundColor: () => {
              return this.green;
            }
          }
        ]
      };
      let extraOptions = {
        ...this.baseStackedChartOptions,
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                drawBorder: true,
                color: "rgba(29,140,248,0.0)"
              },
              scaleLabel: {
                display: true,
                labelString: "Concept",
                fontStyle: "bold",
                fontSize: "16"
              },
              ticks: {
                display: false,
                beginAtZero: true,
                fontColor: "#9a9a9a"
                // callback: function(value) {
                //   if (Number.isInteger(value)) {
                //     return `${value}`;
                //   }
                // }
              }
            }
          ],

          xAxes: [
            {
              stacked: true,
              position: "top",
              gridLines: {
                drawBorder: true,
                color: "rgba(225,78,202,0.1)"
              },
              scaleLabel: {
                display: true,
                labelString: "Number of Users",
                fontStyle: "bold",
                fontSize: "16"
              },
              ticks: {
                display: true,
                fontColor: "#9a9a9a",
                stepSize: this.totalLearners / 8,
                max: this.totalLearners,
                callback: value => {
                  if (Number.isInteger(value)) {
                    return `${((value / this.totalLearners) * 100).toFixed(
                      0
                    )}%`;
                  }
                }
              }
            }
          ]
        }
      };
      this.drChart.data = { ...data };
      this.drChart.extraOptions = { ...extraOptions };
    }
  },
  watch: {
    activeModal: {
      handler: function(t, e) {
        // console.log("T & E", t, e);
        if (t) {
          this.$nextTick(() => {
            this.horizontalStackedChart();
          });
        }
      }
    }
  }
};
</script>
